import * as React from 'react';
import {useFormContext} from 'react-hook-form';

import {AccountSettingsContext} from '../../context/AccountSettingsContext';
import {InputStyled, SelectStyled} from '../../elements/forms/InputContainer';
import {SelectContainer} from './AccountContainer.style';
import {
  AccountUser,
  QueryAccountUsers,
} from '../../../services/alderaan/QueryAccountUsers';
import {MutationAccountUser} from '../../../services/alderaan/MutationAccountUser';
import {
  ACCOUNT_USER_RULES,
  NewAccountUserSchema,
} from './AccountUsers.schema';
import {showToast} from '../../utils/toast.util';

const SEARCH_DEBOUNCE_MS = 400;

const EMPTY_NEW_USER = {email: '', name: '', lastName: '', rule: 'aluno'};

export const AccountUsersComponent = (): JSX.Element => {
  const {account} = React.useContext(AccountSettingsContext);
  const {setValue, watch} = useFormContext();
  const pendingOwner = watch('owner');

  const [users, setUsers] = React.useState<AccountUser[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [term, setTerm] = React.useState<string>('');

  React.useEffect(() => {
    if (!account?.id) {
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      QueryAccountUsers(account.id, term).then((list) => {
        setUsers(list);
        setLoading(false);
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [account?.id, term]);

  const [newUser, setNewUser] = React.useState<any>(EMPTY_NEW_USER);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [formError, setFormError] = React.useState<string>('');

  function updateNewUser(field: string, value: string) {
    setNewUser({...newUser, [field]: value});
  }

  function submitNewUser() {
    const parsed = NewAccountUserSchema.safeParse(newUser);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message || 'Verifique os dados informados');
      return;
    }
    setFormError('');
    setSaving(true);
    MutationAccountUser(account.id, parsed.data)
      .then((created) => {
        setUsers((current) => [
          created,
          ...current.filter((item) => String(item.id) !== String(created.id)),
        ]);
        setNewUser(EMPTY_NEW_USER);
        showToast({html: 'Usuário vinculado à conta com sucesso!'});
      })
      .catch((err) => {
        setFormError(err.message);
        showToast({html: err.message});
      })
      .finally(() => setSaving(false));
  }

  function promoteToOwner(user: AccountUser) {
    setValue('owner', {
      id: user.id,
      name: user.name,
      lastName: user.lastName,
      email: user.email,
    });
  }

  if (!account?.id) {
    return (
      <div className="row">
        <div className="col s12">
          <h2>Usuários</h2>
          <p>Salve a conta para ver e gerenciar os usuários vinculados.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col s12">
        <h2>Usuários</h2>
        <p>Usuários vinculados a esta conta. O owner recebe as comunicações da conta.</p>
      </div>

      <div className="col s12 m6">
        <InputStyled
          label="Buscar por nome ou e-mail"
          value={term}
          onChange={(event: any) => setTerm(event.target.value)}
        />
      </div>

      <div className="col s12">
        <h3>Adicionar usuário</h3>
        <p>
          Se o e-mail já existir na base, o usuário é reaproveitado e apenas
          vinculado a esta conta.
        </p>
      </div>
      <div className="col s12 m3">
        <InputStyled
          label="Nome"
          value={newUser.name}
          onChange={(event: any) => updateNewUser('name', event.target.value)}
        />
      </div>
      <div className="col s12 m3">
        <InputStyled
          label="Sobrenome"
          value={newUser.lastName}
          onChange={(event: any) => updateNewUser('lastName', event.target.value)}
        />
      </div>
      <div className="col s12 m3">
        <InputStyled
          label="E-mail"
          value={newUser.email}
          onChange={(event: any) => updateNewUser('email', event.target.value)}
        />
      </div>
      <SelectContainer className="col s12 m3">
        <SelectStyled
          label="Papel"
          placeholder="Selecione o papel"
          value={ACCOUNT_USER_RULES.find((option) => option.value === newUser.rule)}
          onChange={(option: any) => updateNewUser('rule', option?.value)}
          options={ACCOUNT_USER_RULES}
        />
      </SelectContainer>
      <div className="col s12">
        {!!formError && <p className="red-text">{formError}</p>}
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving}
          onClick={submitNewUser}>
          {saving ? 'Vinculando...' : 'Adicionar usuário'}
        </button>
      </div>

      {pendingOwner?.id && (
        <div className="col s12">
          <p>
            Novo owner selecionado: <strong>{pendingOwner.email}</strong>. Clique
            em Atualizar para confirmar a troca.
          </p>
        </div>
      )}

      <div className="col s12">
        {loading && <p>Carregando usuários...</p>}
        {!loading && !users.length && <p>Nenhum usuário vinculado a esta conta.</p>}
        {!loading && !!users.length && (
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Papel</th>
                <th>Status</th>
                <th>Owner</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{[user.name, user.lastName].filter(Boolean).join(' ')}</td>
                  <td>{user.email}</td>
                  <td>{user.rule}</td>
                  <td>{user.status}</td>
                  <td>{user.isOwner ? 'Sim' : '-'}</td>
                  <td>
                    {!user.isOwner && (
                      <button
                        type="button"
                        className="btn btn-flat"
                        onClick={() => promoteToOwner(user)}>
                        Definir como owner
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
